import { PanelProps } from '@blueprintjs/core';
import * as React from 'react';

import { addDataSource } from 'src/actions/data-sources/action-creators/addDataSource';
import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import {
  DataSourceOption,
  SAMPLE_MUSIC_DATA_SOURCE_TYPE,
} from 'src/constants/dataSourceTypeDescription';
import { SAMPLE_MUSIC_DATA_SOURCE } from 'src/constants/sampleDataSource';
import { DataSourceTypeCards } from 'src/containers/DataSourceCreation/DataSourceTypeCards';
import { DataSourcePanelContainer } from 'src/containers/ProjectResourceDialog/DataSourcePanelContainer';
import { ProjectResourceDialogFooterProps } from 'src/containers/ProjectResourceDialog/types';
import { useLocalVirtualGraph } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useLocalVirtualGraphValue';
import { ProjectVirtualGraphPreviewDialogDataSourceSelectionPanel } from 'src/containers/ProjectVirtualGraphPreviewDialog/ProjectVirtualGraphPreviewDialogDataSourceSelectionPanel';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { isRefreshingDataSourcesSelector } from 'src/selectors/isRefreshingDataSources';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import * as copy from 'src/templates/copy';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type ProjectVirtualGraphPreviewDialogDataSourceTypeSelectionPanelProps = {
  setFooterProps: (footerProps: ProjectResourceDialogFooterProps) => void;
};

export const ProjectVirtualGraphPreviewDialogDataSourceTypeSelectionPanel: React.VFC<
  PanelProps<ProjectVirtualGraphPreviewDialogDataSourceTypeSelectionPanelProps>
> = ({ closePanel, setFooterProps, openPanel }) => {
  const isMounted = useIsMounted();
  const dispatch = useReduxDispatch();

  const connection = useReduxSelector(connectionSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const isRefreshing = useReduxSelector(isRefreshingDataSourcesSelector);
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);
  const isDataSourceDescriptionEndpointSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isDataSourceDescriptionEndpointSupported',
      connection
    )
  );

  const [localVirtualGraph, setLocalVirtualGraph] = useLocalVirtualGraph();
  const [isSampleLoading, setIsSampleLoading] = React.useState(false);

  const handleSelectDataSourceType = React.useCallback(
    async (selectedDataSourceType: string) => {
      if (!selectedDataSourceType) {
        return;
      }

      if (
        localVirtualGraph &&
        selectedDataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE
      ) {
        const existingDataSource = dataSourcesByType[
          SAMPLE_MUSIC_DATA_SOURCE.type
        ]?.find(({ id }) => id === SAMPLE_MUSIC_DATA_SOURCE.id);

        setIsSampleLoading(true);
        if (existingDataSource) {
          // mimic what DataSourceSelectionPanel does in handleSelectionOfDataSource
          await dispatch(refreshDataSourceTables(SAMPLE_MUSIC_DATA_SOURCE.id));
        } else {
          // addDataSource refreshes data source tables
          const didSucceed = await dispatch(
            addDataSource(SAMPLE_MUSIC_DATA_SOURCE)
          );
          if (!didSucceed) {
            setIsSampleLoading(false);
            return;
          }
        }
        setIsSampleLoading(false);
        setLocalVirtualGraph({
          ...localVirtualGraph,
          dataSourceEndpoint:
            SAMPLE_MUSIC_DATA_SOURCE.options[DataSourceOption.ENDPOINT],
          dataSourceId: SAMPLE_MUSIC_DATA_SOURCE.id,
          dataSourceType: SAMPLE_MUSIC_DATA_SOURCE.type,
          from: {
            name: '',
            text: '',
            value: '',
          },
          fields: [],
          previewRows: [],
          errors: undefined,
          isSettingsOpenKey: Date.now(),
        });
        closePanel();
        return;
      }

      const dataSources = dataSourcesByType[selectedDataSourceType] || [];
      if (!isDataSourceDescriptionEndpointSupported && !dataSources.length) {
        return;
      }

      if (connection) {
        dataSources.forEach((dataSource) => {
          if (!dataSource.options) {
            dataSourcesStardogRequestDispatchers.requestDataSourceOptions(
              dataSource.id,
              connection
            );
          }
        });
      }

      const props = {
        closePrevPanel: closePanel,
        setFooterProps,
        type: selectedDataSourceType,
      };

      openPanel({
        renderPanel: ProjectVirtualGraphPreviewDialogDataSourceSelectionPanel,
        props,
      });
    },
    [
      closePanel,
      connection,
      dataSourcesByType,
      dispatch,
      isDataSourceDescriptionEndpointSupported,
      localVirtualGraph,
      openPanel,
      setFooterProps,
      setLocalVirtualGraph,
    ]
  );

  React.useEffect(() => {
    if (isMounted.current) {
      setFooterProps({
        handleBackButtonClick: closePanel,
      });
    }
  }, [closePanel, isMounted, setFooterProps]);

  return (
    <DataSourcePanelContainer
      warningMessage={copy.components.virtualGraphPreviewDialog.warningTitle}
    >
      <DataSourceTypeCards
        isLoading={isConnecting || isRefreshing}
        isSampleLoading={isSampleLoading}
        onSelectDataSourceType={handleSelectDataSourceType}
      />
    </DataSourcePanelContainer>
  );
};
